<template>
  <div class="location-card" ref="cardRef">
    <!-- Animated World Map Section -->
    <div class="location-card__map" ref="mapContainer">
      <img src="@/assets/images/world.svg" alt="World Map" class="world-map" ref="worldMapRef" />
      <!-- Location pin that appears after animation -->
      <div class="map-pin" ref="pinRef" :class="{ 'is-visible': showPin }">
        <div class="pin-marker"></div>
        <div class="pin-pulse"></div>
      </div>
    </div>

    <!-- Location Info -->
    <div class="location-card__info" ref="infoRef" :class="{ 'is-visible': showInfo }">
      <span class="location-card__label">{{ $t('page.about.locationCard.label') }}</span>
      <h4 class="location-card__city">Curitiba - PR</h4>
      <span class="location-card__country">{{ $t('page.about.locationCard.country') }} 🇧🇷</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import gsap from 'gsap'

const cardRef = ref(null)
const mapContainer = ref(null)
const worldMapRef = ref(null)
const pinRef = ref(null)
const infoRef = ref(null)

const showPin = ref(false)
const showInfo = ref(false)

let hasAnimated = false
let observer = null

// Brazil (Curitiba) position in the SVG (viewBox 0 0 2000 857)
// Adjusted coordinates for Curitiba
const BRAZIL_X_PERCENT = 35.7
const BRAZIL_Y_PERCENT = 77.0

// SVG viewBox aspect ratio
const SVG_ASPECT_RATIO = 2000 / 860

const animateMap = () => {
  if (hasAnimated || !worldMapRef.value || !mapContainer.value) return
  hasAnimated = true

  const container = mapContainer.value
  const img = worldMapRef.value
  const containerWidth = container.offsetWidth
  const containerHeight = container.offsetHeight

  // Initial: make the image cover the container
  // Calculate width needed to cover container while maintaining aspect ratio
  const containerAspect = containerWidth / containerHeight

  let initialWidth, initialHeight
  if (containerAspect > SVG_ASPECT_RATIO) {
    // Container is wider than SVG - fit by width
    initialWidth = containerWidth * 1.2
    initialHeight = initialWidth / SVG_ASPECT_RATIO
  } else {
    // Container is taller than SVG - fit by height
    initialHeight = containerHeight * 1.2
    initialWidth = initialHeight * SVG_ASPECT_RATIO
  }

  // Center the initial image
  const initialLeft = (containerWidth - initialWidth) / 2
  const initialTop = (containerHeight - initialHeight) / 2

  // Final: zoom 5x from initial size
  const zoomFactor = 5
  const finalWidth = initialWidth * zoomFactor
  const finalHeight = initialHeight * zoomFactor

  // Position Brazil at center of container
  const brazilX = finalWidth * (BRAZIL_X_PERCENT / 100)
  const brazilY = finalHeight * (BRAZIL_Y_PERCENT / 100)
  const finalLeft = containerWidth / 2 - brazilX
  const finalTop = containerHeight / 2 - brazilY

  // Apply initial state IMMEDIATELY to prevent jump
  gsap.set(img, {
    width: initialWidth,
    height: initialHeight,
    left: initialLeft,
    top: initialTop,
    position: 'absolute',
    willChange: 'width, height, left, top',
  })

  // Animation timeline with delay
  const timeline = gsap.timeline({ delay: 2 })

  timeline
    // Single fluid movement
    .to(img, {
      width: finalWidth,
      height: finalHeight,
      left: finalLeft,
      top: finalTop,
      duration: 2.5,
      ease: 'power4.inOut',
      onComplete: () => {
        showPin.value = true
        // Remove hint after animation to free memory
        gsap.set(img, { willChange: 'auto' })

        gsap.fromTo(
          pinRef.value,
          { scale: 0, y: -15 },
          {
            scale: 1,
            y: 0,
            duration: 0.6,
            ease: 'elastic.out(1, 0.5)',
            onComplete: () => {
              showInfo.value = true
            },
          }
        )
      },
    })
}

const setupObserver = () => {
  if (!cardRef.value) return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateMap()
          observer?.disconnect()
        }
      })
    },
    { threshold: 0.5 }
  )

  observer.observe(cardRef.value)
}

onMounted(() => {
  setupObserver()
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.location-card {
  @apply h-full flex flex-col overflow-hidden;
}

.location-card__map {
  @apply flex-1 relative overflow-hidden;
  min-height: 80px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(129, 140, 248, 0.02) 100%);
}

.world-map {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: none; /* Critical: allows zooming beyond container width */
  filter: brightness(0.6) saturate(0.5);
  opacity: 0.7;
  will-change: width, height, left, top;
}

/* Pin positioned at center of container (Brazil will be centered there) */
.map-pin {
  @apply absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;
}

.map-pin.is-visible {
  opacity: 1;
}

.pin-marker {
  @apply w-3 h-3 rounded-full;
  background: var(--color-electric-purple);
  box-shadow: 0 0 12px 2px rgba(139, 92, 246, 0.8);
  position: relative;
  z-index: 2;
}

.pin-pulse {
  @apply absolute inset-0 rounded-full;
  background: var(--color-electric-purple);
  animation: pin-pulse 1.5s infinite;
  z-index: 1;
}

@keyframes pin-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

/* Location Info */
.location-card__info {
  @apply p-4;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.4s ease;
}

.location-card__info.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.location-card__label {
  @apply text-xs uppercase tracking-wider block mb-1;
  color: var(--color-text-muted);
}

.location-card__city {
  @apply text-lg font-bold;
  color: var(--color-text-primary);
}

.location-card__country {
  @apply text-sm;
  color: var(--color-text-secondary);
}
</style>
